import { useEffect, useMemo, useRef, useState } from 'react';

import type { Resource } from '@setvi/shared/interfaces';

import { useSubscribedMutation } from 'Hooks/React-Query';

import {
  useQrCodes,
  generateDynamicQrCodeMutation
} from '../../../api/qr-codes';
import type { PrintOrderItemCustomization } from '../../../types';
import { parseMachineNumbers } from '../../../utils';

interface UseQrSectionArgs {
  resource: Resource;
  initial?: PrintOrderItemCustomization;
  /** False for assets that do not take QR codes; skips the node-id fetch. */
  enabled: boolean;
}

/**
 * The QR sticker sheet: which machines it covers and the generated PDF.
 *
 * Editing the input never touches the generated sheet — the preview keeps
 * showing the last one until the rep regenerates, and the mismatch is what
 * blocks Add to Cart in the meantime.
 */
export const useQrSection = ({
  resource,
  initial,
  enabled
}: UseQrSectionArgs) => {
  const [numbers, setNumbers] = useState(initial?.qrNumbers ?? '');
  // Machines of the last GENERATED sheet, one QR page each.
  const [machines, setMachines] = useState<string[]>(() =>
    parseMachineNumbers(initial?.qrNumbers ?? '')
  );
  // Seeded from a restored cart item so the preview survives a refresh without
  // regenerating. The filename is sent on sync as DynamicPdfFileName.
  const [sheetUrl, setSheetUrl] = useState(initial?.qrSheetUrl);
  const [fileName, setFileName] = useState(initial?.qrFileName);

  const { mutateAsync: generateQrCode, isLoading: generating } =
    useSubscribedMutation(generateDynamicQrCodeMutation());

  // Node ids are fetched for QR-capable assets and prefill the input once; they
  // do not auto-generate, the rep still clicks Generate.
  const { qrData, loading: fetchingNodes } = useQrCodes(resource.ID, enabled);
  const prefilledRef = useRef(false);

  useEffect(() => {
    if (!enabled || prefilledRef.current) return;
    const nodeIds = qrData?.NodeIds ?? [];
    if (!nodeIds.length) return;

    if (!numbers.trim()) setNumbers(nodeIds.join(', '));
    prefilledRef.current = true;
  }, [enabled, qrData, numbers]);

  // The chips mirror the input, not the generated sheet.
  const inputMachines = useMemo(() => parseMachineNumbers(numbers), [numbers]);

  return {
    numbers,
    // Digits, commas and single spaces only — e.g. "1,2,3" or "1, 2, 3".
    setNumbers: (value: string) =>
      setNumbers(value.replace(/[^0-9, ]/g, '').replace(/ {2,}/g, ' ')),
    inputMachines,
    machines,
    sheetUrl,
    fileName,
    /** The generated sheet matches what is in the input right now. */
    isCurrent: inputMachines.join(',') === machines.join(','),
    loading: fetchingNodes || generating,
    generating,

    /** Removing a chip only edits the input; the sheet stays until regenerated. */
    removeMachine: (machine: string) =>
      setNumbers(inputMachines.filter(m => m !== machine).join(', ')),

    /**
     * Generate the sheet for the entered node ids. The API returns one PDF with
     * a page per node. Resolves true when a sheet came back.
     */
    generate: async (): Promise<boolean> => {
      const nodes = parseMachineNumbers(numbers);
      if (!nodes.length) return false;

      try {
        const data = await generateQrCode({
          resourceId: resource.ID,
          nodeIds: nodes
        });
        setSheetUrl(data?.DownloadURLWithSas ?? undefined);
        setFileName(data?.FileName ?? undefined);
        setMachines(nodes);
        return true;
      } catch {
        // Error toast handled globally by the mutation cache.
        return false;
      }
    },

    /** Drop the generated sheet, keeping the input so a regenerate is enough. */
    clear: () => {
      setSheetUrl(undefined);
      setFileName(undefined);
      setMachines([]);
    }
  };
};
